import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SetupPageLayout from "./components/SetupPageLayout";
import {
  SetupProgressTracker,
  SetupStep,
  type SetupStepDefinition,
  steps as baseSteps,
} from "./components/SetupProgressTracker";
import MicCheck from "./components/MicCheck";
import CameraCheck from "./components/CameraCheck";
import ScreenSharing from "./components/ScreenSharing";
import MobileWebcamStep from "./components/MobileWebcamStep";
import PermissionModal from "./components/PermissionModal";
import SpeedTestWarningModal from "./components/modals/SpeedTestWarningModal";
import { usePermissions } from "./hooks/usePermissions";
import { useSpeedTest } from "./hooks/useSpeedTest";
import { usePrepareInterview } from "./hooks/usePrepareInterview";
import { useEntireScreenShareDetection } from "./hooks/useEntireScreenShareDetection";
import { useMultipleScreensDetection } from "./hooks/useMultipleScreensDetection";
import type {
  InterviewData,
  InterviewStatus,
  PrepareInterviewResponse,
} from "@/api/interviewApi";
import { useMobilePeerConnection } from "@/context/MobilePeerConnectionContext";
import useModelsforHandsDetections from "@/hooks/useModelsForHandsDetections";
import SetupCompleteScreen from "./components/SetupCompleteScreen";
import { ArrowLeft } from "lucide-react";
import { navigateTo } from "@/router/Router";
const MIN_DOWNLOAD_MBPS = 3;
const MIN_UPLOAD_MBPS = 1;

const dev_mode = {
  mic_check_enabled: true,
  camera_check_enabled: true,
  screen_share_enabled: true,
  mobile_webcam_enabled: true,
};

const instructions: Record<SetupStep, string[]> = {
  [SetupStep.MIC_CHECK]: [
    "Allow permissions",
    "Select the microphone",
    "Say the sentence clearly",
  ],
  [SetupStep.CAMERA_CHECK]: [
    "Allow permissions",
    "Select camera source",
    "Keep your face centered on the screen",
    "Make sure you are in a well-lit space",
  ],
  [SetupStep.SCREEN_SHARE]: [
    "Allow permissions",
    "Ensure you share Entire Screen",
  ],
  [SetupStep.MOBILE_WEBCAM]: [
    "Scan the QR code using your phone",
    "Tap Connect on the mobile setup page",
    "Point your phone at your laptop + hands",
    "Leave the phone in place throughout the interview",
  ],
};

const findStepIndex = (stepId: SetupStep, stepList: SetupStepDefinition[]) =>
  stepList.findIndex((step) => step.id === stepId);

const DEFAULT_MIC_SENTENCE = "I am ready for the interview";

interface CheckSetupProps {
  interview_id?: string;
  onComplete?: (payload?: {
    interviewStatus?: InterviewStatus;
    interviewData?: InterviewData;
    systemPromptStatus?: PrepareInterviewResponse["systemPromptStatus"];
    resolvedInitStatus?: InterviewStatus;
    preparedInSession?: boolean;
    resumeFromTimestamp?: string;
    screenStream?: MediaStream | null;
    mobileCameraStream?: MediaStream | null;
  }) => void;
  isMobileWebcamRequired?: boolean;
  isMobileWebcamRequirementLoading?: boolean;
}

const CheckSetup: React.FC<CheckSetupProps> = ({
  interview_id,
  onComplete,
  isMobileWebcamRequired = true,
  isMobileWebcamRequirementLoading = false,
}) => {
  const {
    showPermissionNote,
    permissionStatus,
    requestPermissions,
    setShowPermissionNote,
    cameraBlocked,
    micBlocked,
    windowManagementBlocked,
  } = usePermissions();

  // Track guide preparation status
  const {
    isPreparing: isGuidePreparing,
    isReady: isGuideReady,
    interviewStatus,
    interviewData,
    systemPromptStatus,
    resolvedInitStatus,
    preparedInSession,
    resumeFromTimestamp,
  } = usePrepareInterview(interview_id);
  const interviewDataStatus = interviewData?.interview_status;

  const {
    peerConnectionId: contextPeerConnectionId,
    remoteStream: contextRemoteStream,
    isPeerConnected: contextIsPeerConnected,
    isMobileBypassed: contextIsMobileBypassed,
  } = useMobilePeerConnection();

  //loading detection models for hands and objects
  const laptopDetectionModels = useModelsforHandsDetections();
  const resolvedPeerConnectionId = contextPeerConnectionId;

  const [currentStep, setCurrentStep] = useState<SetupStep>(
    SetupStep.MIC_CHECK
  );
  const [isMicComplete, setIsMicComplete] = useState(false);
  const [cameraChecked, setCameraChecked] = useState(false);
  const [isSetupComplete, setIsSetupComplete] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [mobileWebcamConnected, setMobileWebcamConnected] = useState(
    contextIsPeerConnected || contextIsMobileBypassed,
  );
  const [mainCameraStream, setMainCameraStream] = useState<MediaStream | null>(
    null
  );
const [mobileRemoteStream, setMobileRemoteStream] =
  useState<MediaStream | null>(contextRemoteStream ?? null);
const hasStartedInterviewRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const preserveScreenStreamRef = useRef(false);

  useEffect(() => {
    setMobileRemoteStream(contextRemoteStream ?? null);
  }, [contextRemoteStream]);

  useEffect(() => {
    const effectiveConnected =
      contextIsPeerConnected || contextIsMobileBypassed;
    setMobileWebcamConnected(effectiveConnected);
  }, [contextIsPeerConnected, contextIsMobileBypassed]);
  const {
    isTesting: isSpeedTesting,
    testResult,
    showWarning: showSpeedWarning,
    retryTest,
    dismissWarning,
    error: speedTestError,
    currentRetryCount,
    maxRetries,
    hasReachedMaxRetries,
  } = useSpeedTest({
    minDownloadMbps: MIN_DOWNLOAD_MBPS,
    minUploadMbps: MIN_UPLOAD_MBPS,
  });

  const { isEntireScreenShared, showTabWindowWarning } = useEntireScreenShareDetection({
    screenStream,
    isScreenSharing,
  });

  const { hasMultipleScreens } = useMultipleScreensDetection({ autoStart: true });
  const showMultipleScreenWarning = hasMultipleScreens;

  const activeSteps = useMemo(() => {
    if (isMobileWebcamRequired) {
      return baseSteps;
    }
    return baseSteps.filter((step) => step.id !== SetupStep.MOBILE_WEBCAM);
  }, [isMobileWebcamRequired]);

  const getStepIndex = useCallback(
    (stepId: SetupStep) => findStepIndex(stepId, activeSteps),
    [activeSteps]
  );

  const totalSteps = activeSteps.length;
  const currentStepIndex = useMemo(
    () => getStepIndex(currentStep),
    [currentStep, getStepIndex]
  );
  const finalStepIndex = totalSteps - 1;
  const isFinalStep =
    finalStepIndex !== -1 && currentStepIndex === finalStepIndex;

  const handleMicStatusChange = (isComplete: boolean) => {
    setIsMicComplete(isComplete);
  };

  const handleCameraStatusChange = (isSelected: boolean) => {
    setCameraChecked(isSelected);
  };

  const stopScreenSharing = useCallback(() => {
    setIsScreenSharing(false);

    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => track.stop());
      screenStreamRef.current = null;
    }

    setScreenStream(null);
  }, []);

  const handleComeBackLater = useCallback(() => {
    dismissWarning();
    if (isScreenSharing) {
      stopScreenSharing();
    }
    window.history.back();
  }, [dismissWarning, isScreenSharing, stopScreenSharing]);

  const handleShareScreen = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      });

      screenStreamRef.current = stream;
      setScreenStream(stream);
      setIsScreenSharing(true);

      const [videoTrack] = stream.getVideoTracks();
      if (videoTrack) {
        videoTrack.addEventListener(
          "ended",
          () => {
            stopScreenSharing();
          },
          { once: true } as any
        );
      }
    } catch (error) {
      // screen share failure will keep user on current step
    }
  };

  useEffect(() => {
    if (!isMobileWebcamRequired) {
      setMobileWebcamConnected(true);
    }
  }, [isMobileWebcamRequired]);

  useEffect(() => {
    if (isMobileWebcamRequired) {
      return;
    }
    if (currentStep === SetupStep.MOBILE_WEBCAM) {
      const fallbackStep =
        activeSteps[activeSteps.length - 1]?.id ?? SetupStep.SCREEN_SHARE;
      if (fallbackStep !== currentStep) {
        setCurrentStep(fallbackStep);
      }
    }
  }, [isMobileWebcamRequired, currentStep, activeSteps]);

  useEffect(() => {
    return () => {
      if (!preserveScreenStreamRef.current) {
        stopScreenSharing();
      }
      if (mainCameraStream) {
        mainCameraStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stopScreenSharing, mainCameraStream]);

  useEffect(() => {
    if (!interview_id) return;

    const currentStatus = resolvedInitStatus ?? interviewStatus ?? interviewDataStatus;
    if (currentStatus === "completed") {
      navigateTo(`/report/${interview_id}`);
    }
  }, [interviewDataStatus, interviewStatus, resolvedInitStatus, interview_id]);

  // Preserve streams when setup is marked complete (even if we bypass the normal continue handler)
  useEffect(() => {
    if (isSetupComplete) {
      preserveScreenStreamRef.current = true;
    }
  }, [isSetupComplete]);

  const buildCompletionPayload = useCallback(() => {
    return {
      interviewStatus,
      interviewData,
      systemPromptStatus,
      resolvedInitStatus,
      preparedInSession,
      resumeFromTimestamp,
      screenStream: screenStreamRef.current,
      mobilePeerConnectionId: resolvedPeerConnectionId ?? null,
      mobileCameraStream: mobileRemoteStream,
    };
  }, [
    interviewStatus,
    interviewData,
    systemPromptStatus,
    resolvedInitStatus,
    preparedInSession,
    resumeFromTimestamp,
    resolvedPeerConnectionId,
    mobileRemoteStream,
  ]);

  const handleStartInterview = useCallback(() => {
    if (hasStartedInterviewRef.current) {
      return;
    }
    hasStartedInterviewRef.current = true;
    preserveScreenStreamRef.current = true;
    if (onComplete) {
      onComplete(buildCompletionPayload());
    }
  }, [buildCompletionPayload, onComplete]);

  const markSetupComplete = useCallback(() => {
    setIsSetupComplete(true);
    preserveScreenStreamRef.current = true;
  }, []);

  const handleContinue = useCallback(() => {
    const nextStep = activeSteps[currentStepIndex + 1]?.id;
    if (nextStep) {
      setCurrentStep(nextStep);
    } else if (isFinalStep) {
      if (isMobileWebcamRequired) {
        markSetupComplete();
      } else {
        handleStartInterview();
      }
    }
  }, [
    activeSteps,
    currentStepIndex,
    isFinalStep,
    isMobileWebcamRequired,
    markSetupComplete,
    handleStartInterview,
  ]);

  const micStepComplete = isMicComplete || !dev_mode.mic_check_enabled;
  const cameraStepComplete = cameraChecked || !dev_mode.camera_check_enabled;
  const screenShareStepComplete = !dev_mode.screen_share_enabled || (isScreenSharing && isEntireScreenShared && !showTabWindowWarning && !showMultipleScreenWarning);
  const effectiveMobileWebcamConnected = isMobileWebcamRequired
    ? mobileWebcamConnected
    : true;
  const mobileStepComplete = effectiveMobileWebcamConnected;

  const allStepsComplete =
    micStepComplete &&
    cameraStepComplete &&
    screenShareStepComplete &&
    mobileStepComplete;

  const continueDisabled = (() => {
    switch (currentStep) {
      case SetupStep.MIC_CHECK:
        return !micStepComplete;
      case SetupStep.CAMERA_CHECK:
        return !cameraStepComplete;
      case SetupStep.SCREEN_SHARE:
        if (!screenShareStepComplete) {
          return true;
        }
        if (!isMobileWebcamRequired && isFinalStep) {
          return !isGuideReady;
        }
        return false;
      case SetupStep.MOBILE_WEBCAM:
        if (!mobileStepComplete) {
          return true;
        }
        if (!isGuideReady) {
          return true;
        }
        return false;
      default:
        return false;
    }
  })();

  const shouldRenderContinueFooter = !isSetupComplete;

  const screenShareWarnings: string[] = useMemo(() => {
    const warnings: string[] = [];
    if (showTabWindowWarning) {
      warnings.push("Share your entire screen, not just a tab or window.");
    }
    if (showMultipleScreenWarning) {
      warnings.push("Multiple monitors detected. Please use a single monitor before continuing.");
    }
    return warnings;
  }, [showMultipleScreenWarning, showTabWindowWarning]);

  const renderContinueFooter = useCallback(() => {
    // Determine button text based on state
    let buttonText = "Continue";
    const isFinalScreenShareStep =
      currentStep === SetupStep.SCREEN_SHARE &&
      isFinalStep &&
      !isMobileWebcamRequired;
    if (currentStep === SetupStep.MOBILE_WEBCAM || isFinalScreenShareStep) {
      if (!isGuideReady) {
        buttonText = isGuidePreparing
          ? "Preparing guide..."
          : "Preparing guide...";
      } else {
        buttonText = "Start interview";
      }
    }

    return (
      <div className="flex w-full items-end  justify-end gap-3">
          {screenShareWarnings.length > 0 && (
            <div className="flex-1 rounded-md border border-[#F97316] bg-[#FFF7ED] px-4 py-3 text-sm text-[#9A3412] flex items-center gap-2">
            <div className="flex flex-col gap-1">
                {screenShareWarnings.map((warning) => (
                  <div key={warning} className="flex items-start gap-2">
                    <span className="mt-[2px]">•</span>
                    <span>{warning}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="h-11 flex items-center justify-end">
          <button
            className="w-[200px] h-full bg-button text-white rounded disabled:cursor-not-allowed disabled:bg-[#00000033]"
            onClick={handleContinue}
            disabled={continueDisabled}
            type="button"
          >
            {buttonText}
          </button>
        </div>
      </div>
    );
  }, [
    continueDisabled,
    handleContinue,
    currentStep,
    isGuidePreparing,
    isGuideReady, screenShareWarnings,
    isFinalStep,
    isMobileWebcamRequired,
  ]);

  const isStepClickable = (stepId: SetupStep) => {
    if (stepId === SetupStep.MIC_CHECK) return true;
    if (stepId === SetupStep.CAMERA_CHECK) return micStepComplete;
    if (stepId === SetupStep.SCREEN_SHARE)
      return micStepComplete && cameraStepComplete;
    if (stepId === SetupStep.MOBILE_WEBCAM)
      return micStepComplete && cameraStepComplete && screenShareStepComplete;
    return false;
  };

  const handleTrackerStepClick = (stepId: SetupStep) => {
    if (!isStepClickable(stepId)) {
      return;
    }
    if (
      isScreenSharing &&
      currentStep === SetupStep.SCREEN_SHARE &&
      stepId !== SetupStep.SCREEN_SHARE
    ) {
      stopScreenSharing();
    }
    setCurrentStep(stepId);
  };

  const shouldShowPermissionModal = useMemo(() => {
    if (!showPermissionNote) return false;
    if (windowManagementBlocked) return true;

    if (currentStep === SetupStep.SCREEN_SHARE) {
      return windowManagementBlocked;
    }

    if (
      currentStep === SetupStep.MIC_CHECK ||
      currentStep === SetupStep.CAMERA_CHECK
    ) {
      return cameraBlocked || micBlocked;
    }

    return false;
  }, [
    showPermissionNote,
    currentStep,
    windowManagementBlocked,
    cameraBlocked,
    micBlocked,
  ]);

  const modalPermissionContext = useMemo(() => {
    if (windowManagementBlocked || currentStep === SetupStep.SCREEN_SHARE) {
      return {
        cameraBlocked: false,
        micBlocked: false,
        windowManagementBlocked: windowManagementBlocked,
      };
    }

    if (
      currentStep === SetupStep.MIC_CHECK ||
      currentStep === SetupStep.CAMERA_CHECK
    ) {
      return {
        cameraBlocked,
        micBlocked,
        windowManagementBlocked: false,
      };
    }

    return {
      cameraBlocked: false,
      micBlocked: false,
      windowManagementBlocked: false,
    };
  }, [currentStep, cameraBlocked, micBlocked, windowManagementBlocked]);

  useEffect(() => {
    if (windowManagementBlocked) {
      if (!showPermissionNote) {
        setShowPermissionNote(true);
      }
      return;
    }

    if (currentStep === SetupStep.SCREEN_SHARE) {
      if (showPermissionNote) {
        setShowPermissionNote(false);
      }
      return;
    }

    if (
      currentStep === SetupStep.MIC_CHECK ||
      currentStep === SetupStep.CAMERA_CHECK
    ) {
      if ((cameraBlocked || micBlocked) && !showPermissionNote) {
        setShowPermissionNote(true);
      } else if (!cameraBlocked && !micBlocked && showPermissionNote) {
        setShowPermissionNote(false);
      }
      return;
    }

    if (showPermissionNote) {
      setShowPermissionNote(false);
    }
  }, [
    currentStep,
    windowManagementBlocked,
    cameraBlocked,
    micBlocked,
    showPermissionNote,
    setShowPermissionNote,
  ]);

  const renderProgressColumn = () => {
    if (isSetupComplete) {
      return null;
    }

    if (isMobileWebcamRequirementLoading) {
      return (
        <div className="flex w-[141px] flex-shrink-0 flex-col gap-8">
          <div className="h-5 w-[120px] rounded-full bg-slate-200 animate-pulse" />
          <div className="flex flex-col gap-6">
            {activeSteps.map((step) => (
              <div key={step.id} className="flex items-center gap-2">
                <div className="flex h-[28px] w-[28px] flex-shrink-0 items-center justify-center rounded-full border border-slate-200">
                  <div className="h-3 w-3 rounded-full bg-slate-200 animate-pulse" />
                </div>
                <div className="h-4 flex-1 rounded-full bg-slate-200 animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <SetupProgressTracker
        currentStep={currentStep}
        isMicComplete={isMicComplete}
        cameraChecked={cameraChecked}
        isScreenSharing={isScreenSharing}
        mobileConnected={effectiveMobileWebcamConnected}
        onStepClick={handleTrackerStepClick}
        stepsConfig={activeSteps}
      />
    );
  };

  const handleScaleChange = useCallback((_scale: number) => {
    // Reserved for future zoom persistence
  }, []);

  const handleCameraStreamChange = useCallback((stream: MediaStream | null) => {
    setMainCameraStream((prev) => {
      if (prev) {
        prev.getTracks().forEach((track) => track.stop());
      }

      if (!stream || stream.getTracks().length === 0) {
        return null;
      }

      const clonedTracks = stream.getTracks().map((track) => track.clone());
      return new MediaStream(clonedTracks);
    });
  }, []);

  if (isSetupComplete) {
    return (
      <SetupPageLayout>
        <SetupCompleteScreen
          mainCameraStream={mainCameraStream}
          mobileCameraStream={mobileRemoteStream}
          audioWorking={true}
          cameraWorking={Boolean(mainCameraStream)}
          screenShareActive={Boolean(screenStreamRef.current)}
          mobileConnected={effectiveMobileWebcamConnected}
          networkStable={true}
          onStartInterview={handleStartInterview}
          onExit={() => window.history.back()}
        />
      </SetupPageLayout>
    );
  }

  return (
    <SetupPageLayout>
      <PermissionModal
        isOpen={shouldShowPermissionModal}
        onClose={() => setShowPermissionNote(false)}
        onRequestPermissions={requestPermissions}
        permissionStatus={permissionStatus}
        cameraBlocked={modalPermissionContext.cameraBlocked}
        micBlocked={modalPermissionContext.micBlocked}
        windowManagementBlocked={modalPermissionContext.windowManagementBlocked}
      />
      <SpeedTestWarningModal
        isOpen={showSpeedWarning}
        isTesting={isSpeedTesting}
        downloadMbps={testResult?.downloadMbps ?? 0}
        uploadMbps={testResult?.uploadMbps ?? 0}
        minDownloadMbps={MIN_DOWNLOAD_MBPS}
        minUploadMbps={MIN_UPLOAD_MBPS}
        onTestAgain={retryTest}
        onComeBackLater={handleComeBackLater}
        currentRetryCount={currentRetryCount}
        maxRetries={maxRetries}
        hasReachedMaxRetries={hasReachedMaxRetries}
        error={speedTestError}
      />

      <div
        className={`flex w-full items-start ${
          isSetupComplete ? "" : "gap-20 justify-center"
        }`}
      >
        {!isSetupComplete && renderProgressColumn()}

        <div className="flex-1 flex flex-col gap-10">
          {currentStep === SetupStep.MIC_CHECK && (
            <div className="flex flex-col w-full items-start gap-6">
              <div className="flex flex-col items-start gap-[6px] w-full max-w-[863px]">
                <h2 className="text-[24px] text-[#202326] leading-[33px] font-ubuntu">
                  Microphone Check
                </h2>
                <p className="text-[14px] text-[#686B6D] leading-[21px]">
                  Say the sentence below to test your mic
                </p>
              </div>
              <MicCheck
                onMicQualityChange={handleMicStatusChange}
                instructions={instructions[SetupStep.MIC_CHECK]}
                sentence={DEFAULT_MIC_SENTENCE}
                footer={
                  shouldRenderContinueFooter
                    ? renderContinueFooter()
                    : undefined
                }
              />
            </div>
          )}

          {currentStep === SetupStep.CAMERA_CHECK && (
            <div className="flex flex-col w-full items-start gap-6">
              <div className="flex flex-col items-start gap-[6px] w-full max-w-[863px]">
                <h2 className="text-[24px] text-[#202326] leading-[33px] font-ubuntu">
                  Camera Check
                </h2>
                <p className="text-[14px] text-[#686B6D] leading-[21px]">
                  Make sure you're clearly visible
                </p>
              </div>
              <CameraCheck
                onCameraChange={handleCameraStatusChange}
                onScaleChange={handleScaleChange}
                instructions={instructions[SetupStep.CAMERA_CHECK]}
                onCameraStreamChange={handleCameraStreamChange}
                footer={
                  shouldRenderContinueFooter
                    ? renderContinueFooter()
                    : undefined
                }
              />
            </div>
          )}

          {currentStep === SetupStep.SCREEN_SHARE && (
            <div className="flex flex-col w-full items-start gap-6">
              <div className="flex flex-col items-start gap-[6px] w-full max-w-[863px]">
                <h2 className="text-[24px] text-[#202326] leading-[33px] font-ubuntu">
                  Share Your Screen
                </h2>
                <p className="text-[14px] text-[#686B6D] leading-[21px]">
                  We'll record your screen during the interview to verify your
                  work
                </p>
              </div>
              <ScreenSharing
                isScreenSharing={isScreenSharing}
                screenStream={screenStream}
                stopScreenSharing={stopScreenSharing}
                handleShareScreen={handleShareScreen}
                videoRef={videoRef}
                instructions={instructions[SetupStep.SCREEN_SHARE]}
                isGuidePreparing={isGuidePreparing}
                isGuideReady={isGuideReady}
                allStepsComplete={allStepsComplete}
                footer={
                  shouldRenderContinueFooter
                    ? renderContinueFooter()
                    : undefined
                }
              />
            </div>
          )}

          {currentStep === SetupStep.MOBILE_WEBCAM &&
            isMobileWebcamRequired && (
              <div className="flex flex-col w-full items-start gap-6">
                <div className="flex flex-col items-start gap-[6px] w-full max-w-[863px]">
                  <h2 className="text-[24px] text-[#202326] leading-[33px] font-ubuntu">
                    Connect Your Mobile Device
                  </h2>
                  <p className="text-[14px] text-[#686B6D] leading-[21px]">
                    Use your smartphone to monitor your workspace and ensure a
                    verified interview.
                  </p>
                </div>
                <MobileWebcamStep
                  instructions={instructions[SetupStep.MOBILE_WEBCAM]}
                  onSetupComplete={markSetupComplete}
                  handDetector={laptopDetectionModels.handDetector}
                  objectDetector={laptopDetectionModels.objectDetector}
                  detectionStatusMessage={laptopDetectionModels.statusMessage}
                  isDetectionPreparing={laptopDetectionModels.isPreparing}
                  detectionError={laptopDetectionModels.error}
                  footer={
                    shouldRenderContinueFooter
                      ? renderContinueFooter()
                      : undefined
                  }
                />
              </div>
            )}
        </div>
      </div>
    </SetupPageLayout>
  );
};

export default CheckSetup;
